/**
 * Dynamic static page renderer for /p/:slug.
 *
 * Fetches the page by slug + current UI language, renders the
 * Return link + h1 title + body. For 'general' type pages, the
 * body is rendered as straight markdown or a structure walk. For
 * 'form' type pages, PageContentRenderer walks the structure with
 * the rule engine.
 *
 * Browser tab title is set to "{page title} - {system name}" via the
 * useEffect at the bottom — restored to the previous title on
 * component unmount.
 *
 * 404 handling: if the API returns 404 (page doesn't exist OR is
 * unpublished OR has no content in any language), the component
 * renders a friendly "Page not found" message. Could be elevated
 * to a dedicated 404 component later if we want consistent treatment
 * across the platform.
 */
import { goTo } from '../components/SpaLinks';
import { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import MarkdownContent from '../components/MarkdownContent';
import FaqAccordion from '../components/FaqAccordion';
import BackLink from '../components/BackLink';
import PageContentRenderer from '../components/PageContentRenderer';
import { splitFaqBody } from '../lib/markdown.jsx';
// 0.37.99 — Was: `import { getPage } from '../api/pages'`, called
// directly from useEffect. Now: use fetchPage from pageCache
// which serves cached structure synchronously (repeat visits) and
// fires a stale-while-revalidate refresh in the background. Also
// import getCachedPage so useState can seed itself with the
// cached page on first render — no more "Loading…" flash between
// mount and first API response for pages the user has seen before.
import { fetchPage, getCachedPage, isPageFresh } from '../lib/pageCache';
import useHashScroll from '../lib/useHashScroll';
import { readUrlValues, missingRequired } from '../lib/urlValues';
import { loadTargets, takeHandover, missingTarget } from '../lib/valueHandover';
import { isLoginRequired, goToLogin } from '../lib/requireLogin';
import { buttonTemplatesPublicApi } from '../api/admin';
import styles from './StaticPage.module.css';
// 0.39.63 - Resolve =lib() page titles for display.
import { resolveTitleText, useResolvedTitle } from '../lib/resolveTitleText';
// 0.40.08 - Brand text for the browser tab suffix.
// 0.42.55 - The Henaket back-to-top button, a page setting.
import HenaketScrollTop from '../components/HenaketScrollTop';
import { useSystemIdentity, tabTitle } from '../lib/systemIdentity';


export default function StaticPage() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  // 0.42.63 - For sending a bare flow address to its first step.
  const navigate = useNavigate();



  /* 0.42.65 - A page answers at ITS OWN address and nowhere else.
   *
   * Both prefixes used to answer with the same page, because url_prefix
   * decided only which link the platform wrote. 0.42.64 redirected the
   * wrong one; by decision it is now simply not there, so a page set to
   * "services" does not exist under /p/ at all. One address, one page,
   * and nothing to keep in step. */
  const location = useLocation();
  // 0.37.99 — Seed from localStorage cache. When cached, first
  // render already has the structure; PageContentRenderer mounts
  // and labels resolve on frame one instead of waiting ~500-1500ms
  // for the network round-trip.
  /* 0.49.12 - A copy stored in an earlier visit may be out of date (an
     element deleted since): the server's answer is waited for a moment
     (HOLD_MS) and shown at once when it comes in time, so an old copy
     does not flash. Only when the server is slow is the stored copy
     shown meanwhile. A copy already checked in this visit is shown at
     once, as before. */
  const storedInitial = getCachedPage(slug, language);
  const cachedInitial = storedInitial && isPageFresh(slug, language) ? storedInitial : null;
  const [holding, setHolding] = useState(!!storedInitial && !cachedInitial);
  const [page, setPage] = useState(cachedInitial);

  /* 0.42.71 - Placed after BOTH names it reads: `page` above and
     `location`, and still above every return that tests it.
     
     0.42.68 moved it too far up, to the top of the component. It is an
     immediately-invoked function, so it ran there and read `page`
     before that line had executed - the same class of fault it was
     fixing, moved rather than removed. */
  /* 0.42.83 - A page lives under /p/, and only there.
   *
   * The per-page choice between /p/ and /services/ is gone: a flow is a
   * service and lives under /services/, a page is a page. The address
   * now says what the thing is, with nothing to configure and nothing
   * to keep in step. */
  const wrongPrefix = (() => {
    if (!page) return false;
    const used = location.pathname.split('/')[2];
    return used === 'services';
  })();
  const [loading, setLoading] = useState(!cachedInitial);
  // 0.49.27 - The skeleton only after 300 ms of waiting.
  const [skelDue, setSkelDue] = useState(false);
  useEffect(() => {
    if (!loading) { setSkelDue(false); return undefined; }
    const id = setTimeout(() => setSkelDue(true), 300);
    return () => clearTimeout(id);
  }, [loading]);
  const [error, setError] = useState(null);
  // 0.36.76 — Button templates: needed by PageContentRenderer to
  // resolve any button elements whose variant references a template
  // (e.g. `variant: 'template:5'`). Soft-fail to empty array — pages
  // without template-referencing buttons still render fine.
  const [buttonTemplates, setButtonTemplates] = useState(() => buttonTemplatesPublicApi.cached() || []);
  // 0.49.19 - #element_id in the address scrolls to that element.
  useHashScroll(!!page && !loading);
  // 0.49.20 - The address's values (?training=T-12) as url.training.
  // 0.49.21 - With the page's Allowed values applied.
  /* 0.49.54 - Values handed over through the server
     (lib/valueHandover.js): with the page set to take them that way, or
     with values in addresses off for the whole site, the address's own
     values are not read. `hand` is null until the server answered. */
  const [valuesOff, setValuesOff] = useState(null);
  // 0.49.59 - The site's page for Link incomplete.
  const [sitePage, setSitePage] = useState('');
  useEffect(() => { let on = true; loadTargets().then(t => { if (on) { setValuesOff(!!t.off); setSitePage(t.linkIncompletePage || ''); } }); return () => { on = false; }; }, []);
  const declaredUv = (page && page.settings && page.settings.url_values) || [];
  const serverValues = !!page && (page.settings && page.settings.values_source === 'server' || valuesOff === true);
  const [hand, setHand] = useState(null);
  const marked = !!(location.state && location.state.handover);
  useEffect(() => {
    if (!serverValues || !page) return undefined;
    let on = true;
    setHand(null);
    takeHandover('page', page.slug || slug, location.key, marked).then(v => { if (on) setHand(v || {}); });
    return () => { on = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValues, page && page.slug, location.key]);
  const valuesPending = !!page && declaredUv.length > 0 && (valuesOff === null || (serverValues && hand === null));
  const urlVals = useMemo(() => (serverValues
    ? (hand || {})
    : readUrlValues(location.search, declaredUv).values),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [location.search, page, serverValues, hand]);

  useEffect(() => {
    // 0.37.99 — On slug/language change, re-seed from cache before
    // firing the network refresh. Prevents a flash of the old
    // page's content while the new one loads.
    const cached = getCachedPage(slug, language);
    const HOLD_MS = 800;
    let holdTimer = null;
    let stale = false;
    if (cached && isPageFresh(slug, language)) {
      setPage(cached);
      setLoading(false);
      setHolding(false);
      setError(null);
    } else if (cached) {
      setPage(null);
      setLoading(false);
      setHolding(true);
      setError(null);
      holdTimer = setTimeout(() => {
        if (stale) return;
        setPage(p => p || cached);
        setHolding(false);
      }, HOLD_MS);
    } else {
      setPage(null);
      setLoading(true);
      setError(null);
    }
    /* 0.48.34 - An answer for a slug or language that is no longer the
       one shown is dropped. A first visit to /en/p/... starts in the
       site's default language and switches to English a moment later,
       so both pages were fetched, and when the default language's
       answer arrived last the English link showed the other language. */
    fetchPage(slug, language)
      .then((data) => {
        if (stale) return;
        if (holdTimer) clearTimeout(holdTimer);
        setPage(data);
        setLoading(false);
        setHolding(false);
        setError(null);
      })
      .catch((e) => {
        if (stale) return;
        // 0.46.99 - Requires login: to the login page, and back here
        // after signing in.
        if (isLoginRequired(e)) { goToLogin(navigate, location); return; }
        // Only surface error if we don't have a stale copy to
        // show. fetchPage already returns cached on network
        // failure when a copy exists.
        if (holdTimer) clearTimeout(holdTimer);
        if (cached) { setPage(p => p || cached); setHolding(false); }
        if (!cached) {
          /* 0.42.84 - No flow rescue here: /services/ is the flow's own
             route now, so a page that is not found is simply not
             found. */
          setError(e);
          setLoading(false);
        }
      });
    return () => { stale = true; if (holdTimer) clearTimeout(holdTimer); };
  }, [slug, language]);

  // 0.36.76 — Fetch button templates once on mount. The list is
  // small (admin-saved presets, typically a handful) and changes
  // rarely, so refetching per slug/language change isn't needed.
  // Silent failure → empty array means no template:N variants
  // resolve, but the page still renders (buttons fall back to
  // primary, which is at least visible).
  useEffect(() => {
    let cancelled = false;
    buttonTemplatesPublicApi.list()
      .then((data) => {
        if (!cancelled) setButtonTemplates(Array.isArray(data) ? data : []);
      })
      .catch(() => { if (!cancelled) setButtonTemplates([]); });
    return () => { cancelled = true; };
  }, []);

  // 0.39.83 - Resolve a =lib() title, re-resolving when the library
  // cache hydrates (the earlier resolveTitleText-in-effect ran once
  // and could catch the cache empty, leaving the raw =lib() in the
  // tab).
  const resolvedTabTitle = useResolvedTitle(page ? page.title : '', language);
  const systemIdentity = useSystemIdentity();
  useEffect(() => {
    if (!page) return;
    const previous = document.title;
    // 0.40.08 - Suffix comes from the admin's brand text, not the
    // hardcoded CAF product name. With no brand configured the tab
    // shows just the page title rather than another platform's name.
    // 0.44.75 / 0.44.78 - "Page title - System name" (Settings > System
    // name and icon); with no system name, the page title alone.
    document.title = tabTitle(resolvedTabTitle);
    return () => { document.title = previous; };
  }, [page, resolvedTabTitle, systemIdentity]);

  // 0.36.53 — Landing pages are the platform home; they live at /
  // exclusively. If someone navigates to /p/landing (or any other
  // page that has page_type='landing'), redirect to / so the home
  // is the single source of truth.
  // 0.44.22 - AFTER every hook: it returned before useResolvedTitle and
  // the title effect, so going from another /p/ page to the landing
  // page (same component, fewer hooks) crashed with React error #300.
  if (page && page.page_type === 'landing') {
    return <Navigate to="/" replace />;
  }

  // 0.49.12 - A moment while the server's answer is awaited (see HOLD_MS).
  if (holding && !page) {
    return <main className={styles.page} aria-busy="true" />;
  }

  if (loading) {
    // 0.49.27 - Nothing for the first moment: most pages arrive within
    // it, and a skeleton shown for a blink on every move reads as a flash.
    if (!skelDue) return <main className={styles.page} aria-busy="true" />;
    return (
      <main className={styles.page}>
        {/* 0.36.35 — Henaket 12-col grid wrapping. All three return
          * paths (loading / 404 / page) share the same outer
          * container > row > col-12 structure. The body — when it
          * renders via PageContentRenderer — opens its own .row
          * inside this col, which the grid supports as a nested
          * row. General pages render plain content directly inside
          * the col, no extra row needed. */}
        {/* 0.38.02 — Skeleton in place of "Loading…" text. Gives
            the citizen a rough visual of the incoming form
            (heading + fields + button) instead of a text label,
            so the load FEELS more responsive even on first-ever
            visits (repeat visits render from cache immediately,
            so this skeleton is rarely visible). Shimmer animation
            makes it clear something is coming rather than broken. */}
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className={styles.skeleton} aria-live="polite" aria-busy="true">
                {/* 0.49.27 - .sr-only, the class that exists: "visually-hidden"
                    had no style, so the text showed, and as its key
                    (common.loading) where the translation was missing. */}
                <span className="sr-only">{(() => { const v = t('common.loading'); return v && v !== 'common.loading' ? v : 'Loading…'; })()}</span>
                <div className={styles.skelHeading} />
                <div className={styles.skelSub} />
                <div className={styles.skelField} />
                <div className={styles.skelField} />
                <div className={styles.skelField} />
                <div className={styles.skelButton} />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // 0.42.65 - The wrong prefix is not this page's address: not found.
  if (error || !page || wrongPrefix) {
    return (
      <main className={styles.page}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <BackLink to="/">{t('common.return')}</BackLink>
              <h1 className={styles.title}>Page not found</h1>
              <p>The page you're looking for doesn't exist or is not available.</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* 0.49.20 - A value the page requires in its address is missing or not
     valid: a short message instead of the page. */
  const urlMissing = missingRequired(page.settings && page.settings.url_values, urlVals);
  const tx = (k, d) => { const v = t(k); return v && v !== k ? v : d; };
  // 0.49.54 - Waiting for the handed values: nothing yet, so the
  // message does not flash.
  if (valuesPending) return <main className={styles.page} aria-busy="true" />;
  // 0.49.54 - The page the admin chose for a visitor without the values
  // (opened from outside, a bookmark, a refresh, a typed address).
  const missingTo = urlMissing.length > 0
    ? missingTarget((page.settings && page.settings.values_missing) || sitePage, language) : null;
  if (missingTo && missingTo !== location.pathname) return <Navigate to={missingTo} replace />;
  if (urlMissing.length > 0) {
    return (
      <main className={styles.page}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <BackLink to="/">{t('common.return')}</BackLink>
              <h1 className={styles.title}>{tx('common.link_incomplete', 'This link is incomplete')}</h1>
              <p>{tx('common.link_incomplete_text', 'Open this page from the link where you found it.')}</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // 0.36.13 — Pages/Forms unification.
  // Rendering priority:
  //   1. If the translation has a non-empty `structure`, render that
  //      via PageContentRenderer (the unified element list — covers
  //      markdown, title, paragraph, hint, button, separator, faq,
  //      plus input placeholders for form-type pages).
  //   2. Otherwise fall back to the legacy body (Markdown source)
  //      with the existing page-type distinction. This keeps pages
  //      that haven't been migrated rendering correctly.
  const renderBody = () => {
    if (Array.isArray(page.structure) && page.structure.length > 0) {
      return <PageContentRenderer
               structure={page.structure}
               buttonTemplates={buttonTemplates}
               rules={page.rules || []}
               settings={page.settings || null}
               pageSlug={page.slug || ''}
               urlValues={urlVals}
               /* 0.39.95 - When a leading Layer is flush_top, the
                  return control is rendered INSIDE that first band
                  (see leadingFlushLayer below). renderBody is only
                  called from the JSX, after those consts initialize,
                  so referencing them here is safe. */
               topSlot={leadingFlushLayer ? returnControl : null} />;
    }
    if (page.page_type === 'faq') {
      const items = splitFaqBody(page.body);
      if (items.length > 0) {
        return <FaqAccordion items={items} />;
      }
    }
    return <MarkdownContent source={page.body} />;
  };

  // 0.39.95 - A Layer as the form's FIRST element can ask to start
  // immediately under the sub-header stroke (flush_top). When it
  // does, the Return button is drawn INSIDE that band instead of
  // above it, so the colored area reaches the sub-header and covers
  // the return-button row.
  const leadingFlushLayer = Array.isArray(page.structure)
    && page.structure.length > 0
    && page.structure[0]
    && page.structure[0].type === 'layer'
    && page.structure[0].flush_top === true;

  // The return control itself (or null when disabled / unconfigured).
  /* 0.42.55 - Back to top, from settings.scroll_top.
   *
   * A page setting rather than an element: it belongs to the page, like
   * the Return button, and floats above it wherever the citizen has
   * scrolled to. Its label is per-language, as the Return button's is,
   * and may itself be a =lib(...) expression - resolved the same way.
   */

  const scrollTopControl = (() => {
    // `page` is null until the page loads: this runs on that render too.
    const st = page && page.settings && page.settings.scroll_top;
    if (!st || !st.enabled) return null;
    const pick = () => {
      const l = st.label;
      if (typeof l === 'string') return l.trim();
      if (l && typeof l === 'object') {
        if (l[language]) return l[language];
        const first = Object.values(l).find(v => typeof v === 'string' && v.trim());
        return (first || '').trim();
      }
      return '';
    };
    return (
      <HenaketScrollTop
        label={resolveTitleText(pick(), language)}
        showFromPercent={Number.isFinite(st.percent) ? st.percent : 100}
        /* 0.42.56 - 3 when the setting predates this, which is what the
           editor shows for such a page: the number on screen and the
           behaviour of the page must be the same thing. */
        visibleSeconds={Number.isFinite(st.seconds) ? st.seconds : 3}
        onlyWhenScrollingUp={st.up_only !== false}
      />
    );
  })();

  const returnControl = (() => {
    // 0.39.24 - The top return button is FULLY driven by the form's
    // settings.return_button config - there is no "canonical"
    // fallback link anymore.
    //   disabled / unset -> nothing at all.
    //   mode 'back' -> window.history.back().
    //   mode 'url'  -> internal path via Link, external via a real
    //     navigation.
    const rb = page.settings && page.settings.return_button;
    if (!rb || !rb.enabled) return null;
    // 0.39.26 - label may be a per-language dict { lang: text } or a
    // legacy string. Resolve for the citizen's language, falling back
    // to the first non-empty value, then the default.
    const resolveLabel = () => {
      const l = rb.label;
      if (typeof l === 'string') return l.trim();
      if (l && typeof l === 'object') {
        if (l[language]) return l[language];
        const first = Object.values(l).find(v => typeof v === 'string' && v.trim());
        return (first || '').trim();
      }
      return '';
    };
    // 0.39.66 - The per-language label value may itself be a =lib(...)
    // expression - resolve it against the library like the form name.
    const label = resolveTitleText(resolveLabel(), language) || t('common.return');
    if (rb.mode === 'url') {
      const url = (rb.url || '').trim();
      if (!url) return null;
      if (/^https?:\/\//i.test(url)) {
        return (
          <BackLink onClick={() => { goTo(url); }}>
            {label}
          </BackLink>
        );
      }
      return <BackLink to={url}>{label}</BackLink>;
    }
    return (
      <BackLink onClick={() => window.history.back()}>
        {label}
      </BackLink>
    );
  })();

  return (
    <main className={`${styles.page}${leadingFlushLayer ? ` ${styles.pageFlushTop}` : ''}`}>
      {/* 0.36.40 — Header chrome (back link + page title) stays inside
        * a normal .container/.row/.col so it lines up with other page
        * content. The BODY below this is wrapped differently depending
        * on which renderer it goes through:
        *   - Pages with `structure` go through PageContentRenderer
        *     which emits its own <section.page-band> wrappers (each
        *     band has its own internal .container/.row) so colored
        *     bands span the full viewport width.
        *   - Legacy pages (general/legacy via .body) wrap in a single
        *     transparent band below so the visual hierarchy stays
        *     uniform between layered and non-layered pages.
        *
        * 0.36.66 — Auto-displayed `<h1>{page.title}</h1>` REMOVED.
        * Admins manage page headings themselves via the form editor
        * (Title element). The page-metadata title is still used for
        * the browser tab title via document.title, just not rendered
        * on the page anymore. This avoids the duplicate-heading
        * situation when an admin adds a Title element in their
        * structure but the page also auto-prepends a heading. */}
      {/* 0.39.95 - When a leading Layer is flush_top, the return
          control is handed to PageContentRenderer and drawn inside
          that first band instead of here. */}
      {(!leadingFlushLayer && returnControl) && (
        <div className={`container ${styles.returnRow}`}>
          <div className="row">
            <div className="col-12">
              {returnControl}
            </div>
          </div>
        </div>
      )}

      {/* Body: structure-based pages render bands directly. Legacy
        * general/legacy bodies wrap themselves in a transparent band.
        *
        * 0.36.69 — Structure-based pages are wrapped in a `<form>`
        * element so input elements have proper form semantics
        * (FormData support, native validation triggers, browser
        * autofill correctly scoped, etc.). The submit handler
        * preventsDefault for now — there's no public submission
        * endpoint wired yet; submission is a future ship. The form
        * still serves a purpose: groups inputs semantically and
        * scopes their `required` validation properly. */}
      {(Array.isArray(page.structure) && page.structure.length > 0)
        ? (
          <form onSubmit={(ev) => {
            ev.preventDefault();
            // TODO: when a public submission endpoint exists, POST
            // new FormData(ev.currentTarget) to it. For now, just
            // alert so citizens see something happen.
            // eslint-disable-next-line no-alert
            alert('This form is not yet wired for submissions.');
          }}>
            {renderBody()}
          </form>
        )
        : (
          <section className="page-band" style={{ background: 'transparent' }}>
            <div className="container">
              <div className="row">
                <div className="col-12">
                  {renderBody()}
                </div>
              </div>
            </div>
          </section>
        )
      }
      {scrollTopControl}
    </main>
  );
}
